'use client';

import classNames from 'classnames';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { TMenuLinks } from './types';
import HeaderHamburgerToggle from './HeaderHamburgerToggle';
import { portalContainerId } from '@/components/Portal/PortalContainer';
import { getSupportedLocales, LanguageCode, LanguageMap } from '@/utils/locales';
import Icon from '@/components/Icon/Icon';
import { signOut } from 'supertokens-web-js/recipe/session';
import { usePathname, useRouter } from 'next/navigation';
import { getLinkToTranslation } from '@/components/Header/getLinkToTranslation';
import { config } from '@/config';

const Hamburger = ({
    isHamburgerOpen,
    links,
    backLabel,
    className = '',
}: {
    links: TMenuLinks[];
    isHamburgerOpen: boolean;
    backLabel: string;
    className?: string;
}) => {
    const [currentViewPath, setCurrentViewPath] = useState<number[]>([]);
    const currentView: Array<TMenuLinks | TMenuLinks[]> = currentViewPath.reduce<
        Array<TMenuLinks | TMenuLinks[]>
    >((acc, current) => {
        const currentItem = acc[current];

        if (Array.isArray(currentItem)) {
            return currentItem;
        }
        if (currentItem.subMenu && currentItem.subMenu.length === 1) {
            return currentItem.subMenu[0];
        }
        return currentItem.subMenu!;
    }, links);

    const modalRef = useRef<Element | null>(null);
    useEffect(() => {
        modalRef.current = document.getElementById(portalContainerId);
    }, []);

    const prevViewPathRef = useRef<number[]>();
    const [animation, setAnimation] = useState<'forward' | 'backward' | undefined>();
    useEffect(() => {
        if (prevViewPathRef.current) {
            setAnimation(
                prevViewPathRef.current.length < currentViewPath.length ? 'forward' : 'backward',
            );
            setTimeout(() => {
                setAnimation(undefined);
            }, 400);
        }
        prevViewPathRef.current = currentViewPath;
    }, [currentViewPath]);

    if (typeof window === 'undefined' || !modalRef.current) {
        return null;
    }

    const handleHamburgerClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const target = e.target as HTMLElement;
        const isTargetLang = target.closest('[data-lang]') as HTMLElement | null;
        const isTopLevel = target.closest('.main-nav-element') as HTMLAnchorElement | null;
        const isSubLevel = target.closest('.sub-nav-element') as HTMLAnchorElement | null;

        if (isTargetLang) {
            window.dataLayer &&
                window.dataLayer.push({
                    event: 'select_language',
                    select_language: isTargetLang.dataset.lang,
                });
        }

        if (currentViewPath.length === 0 && isTopLevel) {
            const navLinkText = isTopLevel.textContent?.trim() || '';
            const navLinkUrl = isTopLevel.href || null;
            window.dataLayer &&
                window.dataLayer.push({
                    event: 'main_nav_click',
                    nav_link_text: navLinkText,
                    ...(navLinkUrl ? { nav_link_url: navLinkUrl } : {}),
                });
            window.dataLayer &&
                window.dataLayer.push({ event: 'cta_click', cta_type: 'navigation click' });
        } else if (isSubLevel) {
            const navLinkText = isSubLevel.textContent?.trim() || '';
            const navLinkUrl = isSubLevel.href || null;
            window.dataLayer &&
                window.dataLayer.push({
                    event: 'sub_nav_click',
                    nav_link_text: navLinkText,
                    ...(navLinkUrl ? { nav_link_url: navLinkUrl } : {}),
                });
            window.dataLayer &&
                window.dataLayer.push({ event: 'cta_click', cta_type: 'navigation click' });
        }
    };

    return createPortal(
        <div
            className={classNames(
                className,
                'tw-fixed tw-inset-0 tw-px-4 tw-pt-[100px] tw-w-100 tw-h-100 tw-z-10 tw-bg-white',
                {
                    'tw-hidden': !isHamburgerOpen,
                    'tw-block': isHamburgerOpen,
                },
            )}>
            <div className="tw-max-w-[728px] tw-mx-auto tw-relative" onClick={handleHamburgerClick}>
                <div
                    className={classNames('tw-absolute tw-inset-0', {
                        'tw-animate-appear-from-right': animation === 'forward',
                        'tw-animate-appear-from-left': animation === 'backward',
                    })}>
                    {currentViewPath.length > 0 && (
                        <div
                            className="tw-border-t tw-border-t-menuBorder tw-text-menuText tw-cursor-pointer tw-text-[18px] tw-leading-[22px] tw-py-[28px] "
                            onClick={() =>
                                setCurrentViewPath((prevState) =>
                                    prevState.slice(0, prevState.length - 1),
                                )
                            }>
                            <Icon
                                type="angleLeft"
                                className="tw-inline tw-fill-menuText tw-mr-2 tw-mb-[4px]"
                            />
                            {backLabel}
                        </div>
                    )}
                    {currentView.map((item, index) => {
                        if (Array.isArray(item)) {
                            return (
                                <div
                                    className="
                                        tw-border-t 
                                        tw-border-t-menuBorder 
                                        tw-flex tw-flex-row 
                                        tw-justify-between t
                                        w-items-center 
                                        tw-py-[28px] tw-text-[18px] tw-leading-[22px] 
                                        tw-text-menuText
                                        tw-cursor-pointer"
                                    onClick={() => {
                                        setCurrentViewPath((prevState) => [...prevState, index]);
                                    }}
                                    key={index}>
                                    <span className="tw-text-left">{item[0].title}</span>
                                    <Icon
                                        type="angleRight"
                                        className="tw-inline tw-fill-menuText tw-mr-2 tw-mb-[4px]"
                                    />
                                </div>
                            );
                        }
                        if (item.subMenu) {
                            return (
                                <div
                                    className={classNames(
                                        'tw-border-t tw-border-t-menuBorder tw-flex tw-flex-row tw-justify-between tw-items-center',
                                        'tw-py-[28px] tw-text-[18px] tw-leading-[22px] tw-text-menuText tw-cursor-pointer',
                                        {
                                            'main-nav-element': currentViewPath.length === 0,
                                        },
                                    )}
                                    onClick={() => {
                                        setCurrentViewPath((prevState) => [...prevState, index]);
                                    }}
                                    key={index}>
                                    <span className="tw-text-left">{item.title}</span>
                                    <Icon
                                        type="angleRight"
                                        className="tw-inline tw-fill-menuText tw-mr-2 tw-mb-[4px]"
                                    />
                                </div>
                            );
                        }
                        if (item.action && typeof item.action === 'function') {
                            return (
                                <Link
                                    key={index}
                                    href="#"
                                    onClick={() => {
                                        item.action && item.action();
                                    }}
                                    className={classNames(
                                        'tw-border-t tw-border-t-menuBorder tw-flex tw-py-[28px] tw-leading-[22px] tw-text-[18px] tw-text-menuText hover:tw-no-underline',
                                    )}>
                                    <div className="tw-pt-[2.5px]">{item.title}</div>
                                </Link>
                            );
                        }
                        if (!/^\/[a-z]{2}\/clienthub\/?$/i.test(item.href)) {
                            return (
                                <Link
                                    key={index}
                                    href={item.href}
                                    className={classNames(
                                        'tw-border-t tw-border-t-menuBorder tw-flex tw-py-[28px] tw-leading-[22px] tw-text-[18px] tw-text-menuText hover:tw-no-underline hover:tw-text-inherit',
                                        {
                                            'main-nav-element': currentViewPath.length === 0,
                                            'sub-nav-element': currentViewPath.length !== 0,
                                        },
                                    )}
                                    {...(item.dataLang ? { 'data-lang': item.dataLang } : {})}>
                                    <div className="tw-pt-[2.5px]">{item.title}</div>
                                </Link>
                            );
                        }
                    })}
                </div>
            </div>
        </div>,
        modalRef.current,
    );
};
const getAccessibilityItem = (lang: string, title: string): TMenuLinks[] => [
    {
        title,
        href: `/${lang}#`,
        action: () => {
            // @ts-ignore
            if (window['loadService']) {
                // @ts-ignore
                window['loadService']();
                window.dataLayer &&
                    window.dataLayer.push({ event: 'cta_click', cta_type: 'accessibility' });
                window.dataLayer && window.dataLayer.push({ event: 'accessibility_click' });
            }
        },
    },
];

const getContactUsItem = (lang: string, title: string): TMenuLinks[] => [
    {
        title,
        href: `/${lang}/contact-us`,
    },
];

const profileItem: TMenuLinks[] = [
    {
        title: 'My Profile',
        href: '/en/clienthub/profile',
    },
];

const logoutItem = (router: ReturnType<typeof useRouter>): TMenuLinks[] => [
    {
        title: 'Logout',
        href: '/en/clienthub#',
        action: async () => {
            await signOut();
            router.refresh();
        },
    },
];

export const HamburgerMenu = ({
    links,
    lang,
    labels,
    isHamburgerOpen,
    setIsHamburgerOpen,
}: {
    links: TMenuLinks[];
    lang?: string;
    labels: { contactUs: string; accessibility: string; chooseLanguage: string; back: string };
    isHamburgerOpen: boolean;
    setIsHamburgerOpen: (isOpen: boolean) => void;
}) => {
    const [hamburgerItems, setHamburgerItems] = useState<TMenuLinks[]>([]);
    const router = useRouter();

    const pathname = usePathname();
    const pathParts = pathname.split('/');
    const isPartnerHub = pathParts[1] === 'clienthub' || pathParts[2] === 'clienthub';

    const handleHamburgerMenuClick = () => {
        setIsHamburgerOpen(!isHamburgerOpen);
    };
    // Fetch supported locales and set sub-items
    useEffect(() => {
        async function fetchData() {
            const locales = getSupportedLocales();
            const languages = [
                {
                    title: labels.chooseLanguage,
                    href: '#',
                    subMenu: [
                        locales.map((locale) => ({
                            title: LanguageMap[locale as LanguageCode].name,
                            href: getLinkToTranslation(LanguageMap[locale as LanguageCode].href),
                            dataLang: LanguageMap[locale as LanguageCode].display,
                        })),
                    ],
                },
            ];
            setHamburgerItems([
                ...links,
                ...(isPartnerHub
                    ? [...profileItem, ...logoutItem(router)]
                    : [
                          //  Will be uncommented when it will work
                          // ...getAccessibilityItem(
                          //     lang ?? config.language.default!,
                          //     labels?.accessibility,
                          // ),
                          ...languages,
                          ...getContactUsItem(lang ?? config.language.default!, labels?.contactUs),
                      ]),
            ]);
        }

        fetchData();
    }, [links, labels]);

    return (
        <>
            <HeaderHamburgerToggle handleClick={handleHamburgerMenuClick} />
            <Hamburger
                className="lg:!tw-hidden"
                links={hamburgerItems}
                backLabel={labels.back}
                isHamburgerOpen={isHamburgerOpen}
            />
        </>
    );
};
